<template>
  <div class="header">
    <div v-if="isInStandardWebEnv()" class="header-left">
      <img
        data-test="top-logo"
        class="top-logo"
        src="@assets/svgs/logo-cloud.svg"
        :alt="$t('owork_base.cloud_services')"
        @click="goHome"
      />
      <div class="top-tile" data-test="top-title" @click="goHome">
        <div class="header-left-title">{{ $t('owork_base.cloud_services') }}</div>
        <div class="header-left-subtitle">{{ $t('owork_base.owork') }}</div>
      </div>
      <div class="header-left-divide"></div>
      <MainHeaderMenu />
    </div>
    <div v-else class="header-left">
      <img class="owork-logo" src="@assets/svgs/icon-owork-logo.svg" :alt="$t('owork_base.owork')" />
      <p class="header-left-owork-title">{{ $t('owork_base.owork') }}</p>
      <MainHeaderMenu />
    </div>
    <div class="h5-header-left">
      <img class="h5-top-logo" src="@assets/svgs/logo-cloud.svg" @click="goHome" />
      <div class="h5-divide"></div>
      <div class="h5-top-title" @click="goMainIndex">
        {{ $t('owork_base.application_name.file_manager') }}
      </div>
    </div>
    <div class="header-right">
      <div v-if="isFileManager" class="header-right-file">
        <div class="search-input-container header-icon-container">
          <div v-show="!isSearchInputDialogShow" class="header-input-seat" @click="clickSearchComponent">
            <img src="@assets/svgs/icon-search.svg" alt="" class="search-icon" />
            <span class="header-input-placeholder">{{ $t('owork_base.search_file') }}</span>
          </div>
          <img
            v-show="!isSearchInputDialogShow"
            class="header-icon h5-header-search-icon"
            src="@assets/h5/svgs/icon-search.svg"
            alt=""
            @click="clickSearchComponent"
          />
          <SearchInputComponent v-show="isSearchInputDialogShow" ref="SearchInputComponentRef" />
        </div>
        <div class="setting-notice">
          <a-tooltip placement="top" :mouse-enter-delay="toolTipsShowInSeconds">
            <template #title>
              <span>{{ $t('owork_base.notice') }}</span>
            </template>
            <NoticeIcon v-bind="$attrs" />
          </a-tooltip>
          <div class="transmission-container header-icon-container" @click="notificationBarClicked">
            <TransmissionBadge ref="transmission_badge" v-bind="$attrs" />
            <a-tooltip placement="top" :mouse-enter-delay="toolTipsShowInSeconds">
              <template #title>
                <span>{{ $t('owork_base.transmission_list') }}</span>
              </template>
            </a-tooltip>
          </div>
          <!-- <div
            v-if="!Env.isRelease() && !Env.isPreRelease() && isInStandardWebEnv() && isWindowPlatform()"
            class="pc-connect"            
          >
            <div @click="openDesktopApplicationsPage">
              <img src="@assets/svgs/icon-pc-connect.svg" alt="" />
              <p>下载跨屏互联</p>
            </div>
          </div> -->
        </div>
      </div>
      <div v-else-if="MicroAppId.STICKY_NOTES === activeAppId" class="setting-notice">
        <a-tooltip placement="top" :mouse-enter-delay="toolTipsShowInSeconds">
          <template #title>
            <span>{{ $t('owork_base.note_tips') }}</span>
          </template>
          <div class="back-notes" @click="openOldNoteApplicationsPage">
            <div>
              <img :src="iconBack" alt="" />
              <p>{{ $t('owork_base.switch_old_note') }}</p>
            </div>
          </div>
        </a-tooltip>
      </div>
      <a-tooltip placement="top" :mouse-enter-delay="toolTipsShowInSeconds">
        <template #title>
          <span>{{ userName }}</span>
        </template>
        <AvatarIcon :src="avatarUrl" :name="userName" v-bind="$attrs" />
      </a-tooltip>
    </div>
  </div>

  <ConfirmDialog :is-visible="needShowConfirmDialog" />
</template>

<script lang="ts" setup>
  import AvatarIcon from './AvatarIcon.vue'
  import NoticeIcon from './NoticeIcon.vue'
  import MainHeaderMenu from './MainHeaderMenu.vue'
  import TransmissionBadge from './TransmissionBadge.vue'
  import { INSTANCE as UserInfoManager } from '@core/userInfo/UserInfoManager'
  import { ref, onMounted, computed } from 'vue'
  import iconAvatarDefault from '@assets/svgs/icon-avatar-default.svg'
  import iconBack from '@assets/svgs/icon-back.svg'
  import { TOOLTIP_SHOW_IN_SECONDS } from '@/components/Constants'
  import { useElectron } from '@core/compositions/Electron'
  import { isDesktop, isInIosAPP } from '@core/Utils'
  import { MicroAppId } from '@core/application/ApplicationConstants'
  import { DEFAULT_SITE_BASE, Router as RouterConstant, PATH_SEPARATOR } from '@/core/Constants'
  import { useOtrack } from '@/core/compositions/Otrack'
  import { TrackEventMap } from '@/core/TrackConstants'
  import { useSharedActiveMenuItem } from '@/core/compositions/ActiveMenuItem'
  import { useSharedSearchInputActive } from '@/core/compositions/SearchInputActive'
  import useAppSwitch from '@core/compositions/UseAppSwitcher'
  import ConfirmDialog from '@components/popwindows/ConfirmDialog.vue'
  import SearchInputComponent from '@components/search/SearchInputComponent.vue'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'
  import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
  import type { UserInfo } from '@/netadapters/CloudAdapter'

  const router = useRouter()
  const { t } = useI18n()
  const isSearchInputDialogShow = useSharedSearchInputActive()

  const { activeAppId, isFileManager } = useSharedActiveMenuItem()
  // 移动端禁用tooltips显示
  const TOOLTIP_SHOW_DELAY_INFINITE = Infinity
  const toolTipsShowInSeconds = ref<number>()
  const { switchApp, needShowConfirmDialog } = useAppSwitch()

  const goHome = () => {
    switchApp(async () => {
      if (
        isInIosAPP() &&
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.returnToApp &&
        window.webkit.messageHandlers.returnToApp.postMessage
      ) {
        window.webkit.messageHandlers.returnToApp.postMessage(null)
      } else {
        window.location.href = DEFAULT_SITE_BASE
      }

      return
    })
  }

  const goMainIndex = () => {
    const currentDeviceId = DeviceManager.getCurrentSelectedDevice()?.id
    const defaultMicroAppPath = Array.of('', RouterConstant.MICRO_APP_MAIN, MicroAppId.FILE_MANAGER).join(
      PATH_SEPARATOR
    )

    if (currentDeviceId) {
      router.push({ path: defaultMicroAppPath, query: { deviceId: currentDeviceId } })
    } else {
      router.push({ path: defaultMicroAppPath })
    }
  }

  const openDesktopApplicationsPage = () => {
    window.open('https://connect.oppo.com/zh-CN', ' _blank')
  }

  const { track } = useOtrack()

  const openOldNoteApplicationsPage = () => {
    track({ eventId: TrackEventMap.BACK_TO_THE_OLD_NOTE })
    window.location.href = '/module.html#/note'
  }

  const isInStandardWebEnv = () => useElectron() === undefined

  const isWindowPlatform = () => {
    const { platform } = navigator

    return platform == 'Win16' || platform == 'Win32' || platform == 'WinCE' || platform == 'Windows'
  }

  const avatarUrl = ref<string>(iconAvatarDefault)
  const userName = computed(() => userInfo.value?.userName || t('owork_base.default_user_name'))
  const userInfo = ref<UserInfo>()
  const SearchInputComponentRef = ref<InstanceType<typeof SearchInputComponent>>()

  const clickSearchComponent = async () => {
    await SearchInputComponentRef.value?.toggleVisible()
  }

  const getUserInfo = async () => {
    userInfo.value = await UserInfoManager.getUserInfo()

    avatarUrl.value = userInfo.value?.avatar?.default || iconAvatarDefault
  }
  const transmission_badge = ref<InstanceType<typeof TransmissionBadge> | null>(null)

  const notificationBarClicked = () => {
    transmission_badge.value?.notificationBarClicked()
  }

  const initToolTipsShowInSeconds = async () => {
    const isDesktopEnv = await isDesktop()

    toolTipsShowInSeconds.value = isDesktopEnv ? TOOLTIP_SHOW_IN_SECONDS : TOOLTIP_SHOW_DELAY_INFINITE
  }

  onMounted(async () => {
    initToolTipsShowInSeconds()
    await getUserInfo()
  })
</script>
<style lang="stylus" scoped>

  .header
    height main_header_height
    z-index $zIndexDefault
    padding 0 main_header_padding
    width 100%
    background #fff
    box-sizing border-box
    flex-align-center(space-between)
    .header-left
      cursor pointer
      flex-align-center()
      height 100%
      .top-tile
        ellipsis()
        line-height 1.2
      .top-logo
        rect(40px, 32px)
        margin-right 8px
      .owork-logo
        width 24px
        height 24px
        margin-right 10px
      .header-left-owork-title
        font-size 16px
        title()
        line-height 21px
      .header-left-title
        title()
        font-size 16px
        margin-bottom 0
      .header-left-subtitle
        color secondary_color
        font-size 12px
        margin-bottom 0
        transform scale(0.8) translateX(-8px)
        font-weight default_font_weight
      .header-left-divide
        margin 0 10px 0 6px
        rect(1px, 16px)
        background-color #dddddd
    .header-right
      flex-align-center()
      .header-input-seat
        background #f3f3f3
        border-radius 18px
        rect(229px, 34px)
        line-height 34px
        padding 0 12px
        cursor text
        flex-align-center()
        .search-icon
          rect(20px)
        .header-input-placeholder
          margin-left 4px
          color rgba(0, 0, 0, 0.4)
      .header-right-file
        flex-align-center()
      .setting-notice
        height 66px
        flex-align-center(flex-end)
        flex-direction row-reverse
        margin-right 24px
        .back-notes
          & > div
            height 36px
            padding-left 8px
            padding-right 10px
        .pc-connect
          position relative
          padding-right 16px
          margin-right 8px
          & > div
            height 32px
            padding-left 12px
            padding-right 16px
            &>p
              font-weight bolder_font_weight
            &::after
              position absolute
              top 8px
              rect(0, 16px)
              right 0
              content ''
              border-left 1px solid rgba(0, 0, 0, 0.06)
        .pc-connect
        .back-notes
          & > div
            background-color #f8f8f9
            border-radius 8px
            flex-align-center()
            cursor pointer
            & > img
              rect(20px)
            & > p
              margin-left 2px
              color default_color
            &:hover
              background-color #e6e6e6
  .time-limit-prompt
    margin-right 24px
    color default_shallow_color
  .transmission-container
    rect(32px)
    border-radius 6px
    flex-all-center()
    margin-right 16px
    position relative
    .transmission-placeholder
      position absolute
      width 100%
      height 100%
      left 0
      top 0
      cursor pointer
      border-radius 6px
      &:hover
        background-color rgba(84, 86, 104, 0.06)

  .search-input-container
    height 34px
    position relative
    margin-right 30px
  .transmission-container.header-icon-container
    &:hover
      background-color rgba(84, 86, 104, 0.06)

  @import '../styles/largeScreenAdaptive/MainHeader.styl'
  @import '../styles/h5Adaptive/MainHeader.styl'
</style>
